import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CAPABILITIES, WHY, PROCESS } from "@/data/content";
import { SectionHead } from "@/components/Blocks";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants, EASE } from "@/components/motion";

export function Capabilities() {
  return (
    <section className="relative border-t border-si-line bg-si-bg2 py-24 sm:py-32" data-testid="capabilities-section">
      <div className="wrap">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7"><SectionHead eyebrow="Capabilities" title={["From first sketch", "to production."]} /></div>
          <Reveal delay={0.1} className="lg:col-span-5"><p className="lead">One connected flow — discovery feeds design, design feeds engineering, engineering feeds deployment and growth. Pick up wherever your product is today.</p></Reveal>
        </div>
        <Stagger className="relative mt-16 grid border-l border-t border-si-line sm:grid-cols-2 lg:grid-cols-4" gap={0.06}>
          {CAPABILITIES.map(([label, to], i) => (
            <motion.div key={label} variants={itemVariants} className="border-b border-r border-si-line">
              <Link to={to} data-testid={`capability-${i}`}
                className="group relative flex h-full min-h-[170px] flex-col justify-between overflow-hidden p-6 transition-colors duration-300 hover:bg-si-surface sm:min-h-[200px] sm:p-7">
                <span className="absolute inset-x-0 top-0 h-[2px] origin-left scale-x-0 bg-[linear-gradient(90deg,#17B6F4,#176BFF,#F98A17)] transition-transform duration-500 group-hover:scale-x-100" />
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-si-cyan">{String(i + 1).padStart(2, "0")}</span>
                  <ArrowUpRight className="h-5 w-5 text-si-muted transition-[transform,color] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-si-cyan" />
                </div>
                <p className="mt-8 text-xl font-bold leading-snug tracking-tight sm:text-2xl">{label}</p>
              </Link>
            </motion.div>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function WhyUs() {
  return (
    <section className="relative overflow-hidden bg-si-paper py-24 text-si-ink sm:py-32" data-testid="why-section">
      <div className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-[radial-gradient(closest-side,rgba(23,182,244,0.18),transparent)]" aria-hidden="true" />
      <div className="wrap relative">
        <SectionHead light eyebrow="Why SparkIdea Interactive" title={["Built with a founder's mindset.", "Engineered for the real world."]} className="!max-w-4xl" />
        <Stagger className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-[#dde3ee] bg-[#dde3ee] md:grid-cols-2">
          {WHY.map(([t, d], i) => (
            <motion.div key={t} variants={itemVariants} className="group relative bg-white p-8 transition-colors duration-300 hover:bg-[#fbfcfe] sm:p-12" data-testid={`why-item-${i}`}>
              <span className="text-[clamp(3.5rem,6vw,5.5rem)] font-extrabold leading-none tracking-[-0.05em] text-transparent [-webkit-text-stroke:1.5px_#c3cddd] transition-colors duration-300 group-hover:[-webkit-text-stroke:1.5px_#176BFF]">0{i + 1}</span>
              <h3 className="mt-6 text-2xl font-bold tracking-tight sm:text-3xl">{t}</h3>
              <p className="mt-4 max-w-md text-base leading-relaxed text-[#475467] sm:text-lg">{d}</p>
            </motion.div>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function Process() {
  const [active, setActive] = useState(0);
  return (
    <section className="relative py-24 sm:py-32" data-testid="process-section">
      <div className="wrap">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8"><SectionHead eyebrow="Our process" title={["A clear path", "from idea to launch."]} /></div>
          <Reveal className="lg:col-span-4 lg:text-right"><Cta to="/how-we-build" variant="secondary" testId="process-cta">See How We Work</Cta></Reveal>
        </div>
        <div className="relative mt-16">
          <div className="absolute left-[27px] top-0 h-full w-px bg-si-line lg:left-0 lg:top-[27px] lg:h-px lg:w-full" aria-hidden="true" />
          <motion.div aria-hidden="true" className="absolute left-0 top-[27px] hidden h-[2px] bg-[linear-gradient(90deg,#17B6F4,#176BFF,#F98A17)] lg:block"
            initial={{ width: "0%" }} whileInView={{ width: `${((active + 1) / PROCESS.length) * 100}%` }} animate={{ width: `${((active + 1) / PROCESS.length) * 100}%` }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }} />
          <ol className="grid gap-8 lg:grid-cols-5 lg:gap-6">
            {PROCESS.map(([t, d], i) => {
              const on = i <= active;
              return (
                <motion.li key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: i * 0.1, duration: 0.7, ease: EASE }} className="relative flex gap-6 lg:block">
                  <button type="button" onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
                    aria-pressed={i === active} data-testid={`process-step-${i}`}
                    className={`relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full border font-mono text-sm transition-[background-color,border-color,color,box-shadow] duration-300 ${on ? "border-si-cyan bg-si-cyan text-si-bg shadow-[0_0_30px_-2px_rgba(23,182,244,0.8)]" : "border-si-line bg-si-bg text-si-muted"}`}>
                    0{i + 1}
                  </button>
                  <div className="lg:mt-8">
                    <h3 className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${i === active ? "text-si-text" : "text-si-text/80"}`}>{t}</h3>
                    <p className="mt-3 text-base leading-relaxed text-si-muted">{d}</p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
